import { createApiClient } from './client'

export interface UserProfile {
  id: string
  user_id: string
  experience_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  trip_preferences: string[]
  bio?: string
  location?: string
  years_overlanding: number
  profile_image_path?: string
  banner_image_path?: string
  instagram_url?: string
  youtube_url?: string
  website_url?: string
  tiktok_url?: string
  facebook_url?: string
  dietary_restrictions?: string[]
  allergies?: string[]
  theme_preference?: 'light' | 'dark' | 'system'
  home_latitude?: number | null
  home_longitude?: number | null
  home_location_label?: string | null
  created_at: string
  updated_at: string
}

export interface UpdateUserProfileDto {
  bio?: string
  location?: string
  experience_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  trip_preferences?: string[]
  years_overlanding?: number
  instagram_url?: string
  youtube_url?: string
  website_url?: string
  tiktok_url?: string
  facebook_url?: string
  dietary_restrictions?: string[]
  allergies?: string[]
  theme_preference?: 'light' | 'dark' | 'system'
  home_latitude?: number
  home_longitude?: number
  home_location_label?: string
}

export interface UserMealStat {
  mealName: string
  tripCount: number
  trips: Array<{
    tripId: string
    tripName: string
    date: string
  }>
}

export interface UserGearStat {
  gearName: string
  tripCount: number
  trips: Array<{
    tripId: string
    tripName: string
    date: string
  }>
}

export interface UserStatsResponse {
  success: boolean
  data: {
    meals: UserMealStat[]
    gear: UserGearStat[]
  }
}

export interface UserContributionDto {
  date: string
  count: number
  contributions: {
    trips: number
    gearAdded: number
    gearClaimed: number
    meals: number
    photos: number
  }
}

export interface UserContributionsResponse {
  success: boolean
  data: UserContributionDto[]
}

export interface DietaryRestrictionsResponse {
  success: boolean
  data: {
    dietary_restrictions: string[]
  }
}

export interface AllergiesResponse {
  success: boolean
  data: {
    allergies: string[]
  }
}

export interface UserProfileApiResponse {
  success: boolean
  data: UserProfile
}

export class UserProfileApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // The name lives in auth metadata, so the profile row doesn't carry it

  async getDisplayName(userId: string): Promise<string | null> {

    const response = await this.apiClient.get<{ success: boolean; data: { display_name: string | null } }>(`/users/${userId}/display-name`)

    return response.data.display_name

  }


  // Get user profile by ID
  async getUserProfile(userId: string): Promise<UserProfile> {
    const response = await this.apiClient.get<UserProfileApiResponse>(`/users/${userId}`)
    return response.data
  }

  // Update user profile
  async updateUserProfile(userId: string, profileData: UpdateUserProfileDto): Promise<UserProfile> {
    const response = await this.apiClient.put<UserProfileApiResponse>(`/users/${userId}`, profileData)
    return response.data
  }

  // Upload profile image
  async uploadProfileImage(userId: string, file: File): Promise<UserProfile> {
    const formData = new FormData()
    formData.append('profile', file)
    const response = await this.apiClient.post<UserProfileApiResponse>(
      `/users/${userId}/profile-image`,
      formData
    )
    return response.data
  }

  // Upload banner image
  async uploadBannerImage(userId: string, file: File): Promise<UserProfile> {
    const formData = new FormData()
    formData.append('banner', file)
    const response = await this.apiClient.post<UserProfileApiResponse>(
      `/users/${userId}/banner`,
      formData
    )
    return response.data
  }

  // Delete profile image
  async deleteProfileImage(userId: string): Promise<void> {
    await this.apiClient.delete(`/users/${userId}/profile-image`)
  }

  // Delete banner image
  async deleteBannerImage(userId: string): Promise<void> {
    await this.apiClient.delete(`/users/${userId}/banner`)
  }

  // Get user stats (meals and gear)
  async getUserStats(userId: string): Promise<{ meals: UserMealStat[]; gear: UserGearStat[] }> {
    const response = await this.apiClient.get<UserStatsResponse>(`/users/${userId}/stats`)
    return response.data
  }

  // Get user contributions
  async getUserContributions(userId: string): Promise<UserContributionDto[]> {
    const response = await this.apiClient.get<UserContributionsResponse>(`/users/${userId}/contributions`)
    return response.data
  }

  // Get user dietary restrictions
  async getUserDietaryRestrictions(): Promise<string[]> {
    const response = await this.apiClient.get<DietaryRestrictionsResponse>('/users/profile/dietary-restrictions')
    return response.data.dietary_restrictions
  }

  // Update user dietary restrictions
  async updateUserDietaryRestrictions(restrictions: string[]): Promise<string[]> {
    const response = await this.apiClient.put<DietaryRestrictionsResponse>(
      '/users/profile/dietary-restrictions',
      { dietary_restrictions: restrictions }
    )
    return response.data.dietary_restrictions
  }

  // Get user allergies (free-form foods the user cannot eat)
  async getUserAllergies(): Promise<string[]> {
    const response = await this.apiClient.get<AllergiesResponse>('/users/profile/allergies')
    return response.data.allergies
  }

  // Update user allergies
  async updateUserAllergies(allergies: string[]): Promise<string[]> {
    const response = await this.apiClient.put<AllergiesResponse>(
      '/users/profile/allergies',
      { allergies }
    )
    return response.data.allergies
  }
}

